import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Spin, Typography } from 'antd';
import { JobApplication } from '../models/job-application.model';
import {
  PipelineColumn,
  findColumnForApplication,
  getPipelineColumnTone,
  groupApplicationsByColumn,
  statusesMatch,
} from '../utils/pipeline-board.utils';
import { formatApplicationSkillScore } from '../utils/application-relevance-labels';
import PipelineStageMoveModal, {
  PipelineStageMoveRequest,
} from './PipelineStageMoveModal';

const { Text } = Typography;

interface Props {
  applications: JobApplication[];
  columns: PipelineColumn[];
  loading?: boolean;
  enableDrag?: boolean;
  companyId?: string;
  clientId?: string;
  resolveMoveTarget?: (
    application: JobApplication,
    targetColumn: PipelineColumn
  ) => Promise<{ targetColumn: PipelineColumn; clientId: string } | null>;
  onApplicationClick: (application: JobApplication) => void;
  onRefresh: () => void | Promise<void>;
}

function formatAppliedDate(appliedOn: string): string {
  if (!appliedOn) {
    return '';
  }
  return appliedOn.split('T')[0];
}

function PipelineCardContent({
  application,
  dragging = false,
}: {
  application: JobApplication;
  dragging?: boolean;
}) {
  const score = formatApplicationSkillScore(application.relevance);
  const applied = formatAppliedDate(application.appliedOn);

  return (
    <article
      className={`ac-pipeline-card${dragging ? ' ac-pipeline-card--dragging' : ''}`}
    >
      <Text strong className="ac-pipeline-card__title">
        {application.candidate || 'Unknown candidate'}
      </Text>
      {application.jobName ? (
        <Text type="secondary" className="ac-pipeline-card__meta">
          {application.jobName}
        </Text>
      ) : null}
      {application.client ? (
        <Text type="secondary" className="ac-pipeline-card__meta">
          {application.client}
        </Text>
      ) : null}
      <div className="ac-pipeline-card__footer">
        {score ? <span className="ac-pipeline-card__pill">{score}</span> : null}
        {applied ? (
          <Text type="secondary" className="ac-pipeline-card__date">
            Applied {applied}
          </Text>
        ) : null}
      </div>
    </article>
  );
}

function DraggablePipelineCard({
  application,
  enableDrag,
  onApplicationClick,
}: {
  application: JobApplication;
  enableDrag: boolean;
  onApplicationClick: (application: JobApplication) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: application.id,
      data: { application },
      disabled: !enableDrag,
    });

  const style = transform
    ? { transform: CSS.Translate.toString(transform) }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`ac-pipeline-card-wrap${isDragging ? ' ac-pipeline-card-wrap--dragging' : ''}`}
      {...(enableDrag ? listeners : {})}
      {...(enableDrag ? attributes : {})}
    >
      <button
        type="button"
        className="ac-pipeline-card-button"
        onClick={() => onApplicationClick(application)}
      >
        <PipelineCardContent application={application} dragging={isDragging} />
      </button>
    </div>
  );
}

function PipelineColumnLane({
  column,
  applications,
  enableDrag,
  onApplicationClick,
}: {
  column: PipelineColumn;
  applications: JobApplication[];
  enableDrag: boolean;
  onApplicationClick: (application: JobApplication) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    disabled: !enableDrag || !column.droppable,
  });

  const tone = getPipelineColumnTone(column.key);

  return (
    <section
      ref={setNodeRef}
      className={`ac-pipeline-column ac-pipeline-column--${tone}${isOver ? ' ac-pipeline-column--active' : ''}${
        !column.droppable ? ' ac-pipeline-column--readonly' : ''
      }`}
      aria-label={`${column.label} (${applications.length})`}
    >
      <header className="ac-pipeline-column__header">
        <Text strong>{column.label}</Text>
        <span className="ac-pipeline-column__count">{applications.length}</span>
      </header>
      <div className="ac-pipeline-column__body">
        {applications.map((application) => (
          <DraggablePipelineCard
            key={application.id}
            application={application}
            enableDrag={enableDrag && column.droppable}
            onApplicationClick={onApplicationClick}
          />
        ))}
      </div>
    </section>
  );
}

const JobApplicationPipelineBoard: React.FC<Props> = ({
  applications,
  columns,
  loading = false,
  enableDrag = false,
  companyId,
  clientId,
  resolveMoveTarget,
  onApplicationClick,
  onRefresh,
}) => {
  const [activeApplication, setActiveApplication] =
    useState<JobApplication | null>(null);
  const [pendingMove, setPendingMove] =
    useState<PipelineStageMoveRequest | null>(null);
  const [moving, setMoving] = useState(false);
  const [pendingClientId, setPendingClientId] = useState<string | undefined>(
    clientId
  );

  const grouped = useMemo(
    () => groupApplicationsByColumn(applications, columns),
    [applications, columns]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveApplication(null);

    if (!enableDrag || !over || active.id === over.id) {
      return;
    }

    const application = applications.find((item) => item.id === active.id);
    const sourceColumn = application
      ? findColumnForApplication(application, columns)
      : undefined;
    const targetColumn = columns.find((column) => column.id === over.id);

    if (
      !application ||
      !targetColumn ||
      !targetColumn.droppable ||
      sourceColumn?.id === targetColumn.id ||
      statusesMatch(application.status, targetColumn.key)
    ) {
      return;
    }

    if (targetColumn.stageId) {
      setPendingClientId(clientId);
      setPendingMove({ application, targetColumn });
      return;
    }

    if (!resolveMoveTarget) {
      return;
    }

    setMoving(true);
    try {
      const resolved = await resolveMoveTarget(application, targetColumn);
      if (resolved?.targetColumn.stageId) {
        setPendingClientId(resolved.clientId);
        setPendingMove({
          application,
          targetColumn: resolved.targetColumn,
        });
      }
    } finally {
      setMoving(false);
    }
  };

  const handleMoveCompleted = async () => {
    setMoving(true);
    try {
      await onRefresh();
    } finally {
      setMoving(false);
    }
  };

  if (loading) {
    return (
      <div className="ac-pipeline-board ac-pipeline-board--loading">
        <Spin tip="Loading pipeline…" spinning>
          <div className="ac-pipeline-board__loading-target" aria-hidden />
        </Spin>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(event) => {
        const application = applications.find(
          (item) => item.id === event.active.id
        );
        setActiveApplication(application ?? null);
      }}
      onDragEnd={(event) => void handleDragEnd(event)}
      onDragCancel={() => setActiveApplication(null)}
    >
      <div
        className={`ac-pipeline-board${moving ? ' ac-pipeline-board--busy' : ''}`}
        style={{ '--pipeline-cols': columns.length } as React.CSSProperties}
        aria-busy={moving}
      >
        {columns.map((column) => (
          <PipelineColumnLane
            key={column.id}
            column={column}
            applications={grouped[column.id] ?? []}
            enableDrag={enableDrag}
            onApplicationClick={onApplicationClick}
          />
        ))}
      </div>

      <DragOverlay>
        {activeApplication ? (
          <PipelineCardContent application={activeApplication} dragging />
        ) : null}
      </DragOverlay>

      <PipelineStageMoveModal
        open={pendingMove !== null}
        pending={pendingMove}
        companyId={companyId}
        clientId={pendingClientId}
        onClose={() => {
          setPendingMove(null);
          setPendingClientId(clientId);
        }}
        onCompleted={handleMoveCompleted}
      />
    </DndContext>
  );
};

export default JobApplicationPipelineBoard;
